import { ToolCallComplete } from "../streaming/anthropic-stream-handler.types";
import {
  AssistantContentBlock,
  isAssistantMessage,
  isToolResultMessage,
  Message,
} from "../streaming/streaming-message-types";

type StoreOptions = {
  getMessages: () => Message[];
  setMessages: (next: Message[]) => void;
  emitMessagesUpdated: (messages: Message[]) => void;
};

export class MessageStore {
  constructor(private readonly opts: StoreOptions) {}

  appendUser(text: string): number {
    const next = [
      ...this.opts.getMessages(),
      {
        role: "user",
        content: text,
        displayState: { kind: "displayed" },
      } as Message,
    ];
    this.opts.setMessages(next);
    this.opts.emitMessagesUpdated([...next]);
    return next.length - 1;
  }

  appendAssistant(params: { hidden?: boolean }): number {
    const hidden = !!params.hidden;
    const msg: Message = {
      role: "assistant",
      content: [],
      toolCalls: [],
      displayState: hidden
        ? { kind: "hidden" }
        : { kind: "displaying", visibleText: "" },
    };
    const next = [...this.opts.getMessages(), msg];
    this.opts.setMessages(next);
    this.opts.emitMessagesUpdated([...next]);
    return next.length - 1;
  }

  updateAssistantDisplay(index: number, visibleText: string): void {
    const prev = this.opts.getMessages();
    const next = [...prev];
    const assistantMessage = next[index];
    if (!isAssistantMessage(assistantMessage)) return;

    let found = false;
    for (let i = assistantMessage.content.length - 1; i >= 0 && !found; i--) {
      const c = assistantMessage.content[i];
      if (c.type === "text") {
        assistantMessage.content[i] = {
          ...c,
          text: visibleText,
        };
        found = true;
        break;
      }
    }

    if (!found) {
      assistantMessage.content.push({ type: "text", text: visibleText });
    }

    next[index] = {
      ...assistantMessage,
      displayState: { kind: "displaying", visibleText },
    };
    this.opts.setMessages(next);
    this.opts.emitMessagesUpdated([...next]);
  }

  reflectToolUseStreaming(index: number, id: string, name: string): void {
    const prev = this.opts.getMessages();
    const next = [...prev];
    const assistantMessage = next[index];
    if (!isAssistantMessage(assistantMessage)) return;
    assistantMessage.content.push({ type: "tool_use", id, name, input: {} });
    next[index] = {
      ...assistantMessage,
    };
    this.opts.setMessages(next);
    this.opts.emitMessagesUpdated([...next]);
  }

  addToolCallRequestComplete(index: number, toolCall: ToolCallComplete): void {
    const prev = this.opts.getMessages();
    const next = [...prev];
    const assistantMessage = next[index];
    if (!isAssistantMessage(assistantMessage)) return;

    assistantMessage.toolCalls = [
      ...(assistantMessage.toolCalls ?? []),
      toolCall,
    ];

    assistantMessage.content = assistantMessage.content.map((c) => {
      if (c.type === "tool_use" && c.id === toolCall.id) {
        return { ...c, input: toolCall.arguments };
      }
      return c;
    });

    next[index] = {
      ...assistantMessage,
    };
    this.opts.setMessages(next);
    this.opts.emitMessagesUpdated([...next]);
  }

  finalizeAssistant(
    index: number,
    params: { text: string; toolCalls?: ToolCallComplete[]; messageId?: string }
  ): void {
    const { text, toolCalls = [], messageId } = params;
    const prev = this.opts.getMessages();
    const next = [...prev];
    const a = next[index];
    if (!a || a.role !== "assistant") return;
    const contentBlocks: Array<{
      type: "text" | "tool_use";
      [k: string]: unknown;
    }> = [];
    if (text.trim()) contentBlocks.push({ type: "text", text });
    for (const tc of toolCalls) {
      contentBlocks.push({
        type: "tool_use",
        id: tc.id,
        name: tc.name,
        input: tc.arguments,
      });
    }
    next[index] = {
      ...a,
      messageId: messageId ?? a.messageId,
      content: contentBlocks as AssistantContentBlock[],
    } as Message;
    this.opts.setMessages(next);
    this.opts.emitMessagesUpdated([...next]);
  }

  insertHiddenToolResults(
    executed: Array<{ toolCallId: string; result: string }>,
    revealImmediately: boolean
  ): number {
    const prev = this.opts.getMessages();
    const toolResultContent = executed.map((tr) => ({
      type: "tool_result" as const,
      tool_use_id: tr.toolCallId,
      content: tr.result,
    }));
    const toolResultMsg: Message = {
      role: "tool",
      content: toolResultContent,
      displayState: revealImmediately
        ? { kind: "displayed" }
        : { kind: "hidden" },
    };
    const next = [...prev, toolResultMsg];
    this.opts.setMessages(next);
    this.opts.emitMessagesUpdated([...next]);
    return next.length - 1;
  }

  revealHidden(kind: "tool_result" | "assistant" | "all" = "all"): void {
    const prev = this.opts.getMessages();
    let changed = false;
    const next = prev.map((m) => {
      const isHidden = m.displayState?.kind === "hidden";
      if (isHidden) {
        if (
          kind === "all" ||
          (kind === "tool_result" && isToolResultMessage(m)) ||
          (kind === "assistant" && isAssistantMessage(m))
        ) {
          changed = true;
          return {
            ...m,
            displayState: {
              kind: "displayed",
            },
          } as Message;
        }
      }
      return m;
    });
    if (changed) {
      this.opts.setMessages(next);
      this.opts.emitMessagesUpdated([...next]);
    }
  }

  setAssistantMessageId(index: number, messageId: string): void {
    const prev = this.opts.getMessages();
    const a = prev[index];
    if (!isAssistantMessage(a) || a.messageId) return;
    const next = [...prev];
    next[index] = { ...a, messageId } as Message;
    this.opts.setMessages(next);
    this.opts.emitMessagesUpdated([...next]);
  }
}
